import { Injectable } from '@angular/core';

import { BehaviorSubject, forkJoin, Observable, of, Subscription, Observer } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { ProminenceGroupModel } from '../../../api/monitoring/model/prominence-group.model';
import { MonitoringAccountConfigurationApi } from '../../../api/monitoring/monitoring-account-configuration.api';
import { ProminenceLockerService } from '../../../Common/Misc/services/prominence-locker.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { AccountPreferencesErrors } from '../enums/account-preferences-errors';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { gateCall } from '../../../Core/rxjs-utils';

@Injectable()
export class AccountPreferencesService {
    subscriptions: Subscription[] = [];

    errors = new AccountPreferencesErrors();
    errors$: BehaviorSubject<AccountPreferencesErrors> = new BehaviorSubject<AccountPreferencesErrors>(this.errors);

    prominenceGroups: ProminenceGroupModel[];
    prominenceGroups$: BehaviorSubject<ProminenceGroupModel[]> = new BehaviorSubject<ProminenceGroupModel[]>(null);
    originalProminenceGroups: ProminenceGroupModel[];
    prominenceGroupLimit$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    changesToSave$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private coreDialogService: CoreDialogService,
        private messageService: MessageService,
        private monitoringAccountConfigurationApi: MonitoringAccountConfigurationApi,
        private prominenceLockerService: ProminenceLockerService,
        private currentUserStateService: CurrentUserStateService,
        private localizationService: LocalizationService,
    ) {
        this.subscriptions.push(
            this.getProminenceGroups().subscribe(),
            gateCall(this.currentUserStateService.hasAdvancedMonitoringAccess()).pipe(
                switchMap(() => this.monitoringAccountConfigurationApi.getMonitoringAccountConfigurationModel())
            ).subscribe(configs => {
                this.prominenceGroupLimit$.next(configs?.prominenceGroupLimit);
            })
        );
    }

    getProminenceGroups(): Observable<any> {
        return gateCall(this.currentUserStateService.hasAdvancedMonitoringAccess()).pipe(
            switchMap(() => this.monitoringAccountConfigurationApi.getProminenceGroups()),
            tap(groups => {
                this.prominenceGroups = groups.map(group => new ProminenceGroupModel(group));
                this.originalProminenceGroups = JSON.parse(JSON.stringify(groups));
                this.prominenceGroups$.next(this.prominenceGroups);
            })
        );
    }

    saveChanges(onConfirmed?: () => void) {
        const apiCalls = [];
        let requireConfirmation = false;

        if (this.changesToSave()) {
            this.prominenceGroups = this.prominenceGroups.map(group => group.setApiModel());
            this.prominenceGroups$.next(this.prominenceGroups);
            requireConfirmation = true;
            apiCalls.push(
                this.monitoringAccountConfigurationApi.updateProminenceGroups(this.prominenceGroups).pipe(map(() => {
                    this.originalProminenceGroups = JSON.parse(JSON.stringify(this.prominenceGroups));
                    this.prominenceLockerService.forceCheck();
                }))
            );
        };
        if (requireConfirmation) {
            this.coreDialogService.open(
                ConfirmDialogComponent,
                {
                    inputs: {
                        heading: 'AccountPreferences.prominence.confirmation.header',
                        messageHtml: 'AccountPreferences.prominence.confirmation.body',
                        okLabel: 'ok',
                        cancelLabel: 'cancel',
                        width: 350
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed) {
                                this.performUpdate(apiCalls);
                                onConfirmed?.();
                            }
                        }
                    }
                }
            );
        } else {
            this.performUpdate(apiCalls);
            onConfirmed?.();
        }
    };

    performUpdate(apiCalls: Observable<any>[]) {
        if (apiCalls.length > 0) {
            this.loading$.next(true);
            forkJoin(apiCalls).pipe(
                tap(() => {
                    this.loading$.next(false);
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        detail: 'AccountPreferences.prominence.update.success.message',
                        summary: 'AccountPreferences.prominence.update.success.heading',
                    });
                }),
                // re-get the prominence groups.. because on an PUT.. the api changes the IDs of the prominence groups
                switchMap(() => this.getProminenceGroups())
            ).subscribe({
                error: (error) => {
                    this.loading$.next(false);

                    // check to see if the unique name error is returned from the back end
                    if (error?.error?.details && (error.error.details[0]?.code === CommonApiErrorEnum.invalidModel)) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            detail: this.localizationService.transform('AccountPreferences.prominence.update.error.uniqueName'),
                            summary: 'AccountPreferences.prominence.update.error.heading',
                        });
                    } else {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            detail: 'AccountPreferences.prominence.update.error.message',
                            summary: 'AccountPreferences.prominence.update.error.heading',
                        });
                    }
                }
            });
        } else {
            this.messageService.add({
                severity: messageSeverityEnum.warning,
                summary: this.localizationService.transform('AccountPreferences.prominence.update.noChanges'),
            });
        }
    }

    /**
     * sets the errors for the account preferences page
     */
    setErrors(errors: AccountPreferencesErrors): void {
        this.errors = errors;
        this.errors$.next(errors);
    }

    /**
     * returns true if there is a duplicate name in the list of prominence subject groups
     */
    isNameDuplicate(name: string, id?: number|string): boolean {
        if (!this.prominenceGroups || !name) {
            return false;
        }
        const duplicateName = this.prominenceGroups.find(subjectGroup => {
            if (subjectGroup.id) {
                return subjectGroup?.name?.toLocaleLowerCase()?.trim() === name.toLocaleLowerCase().trim() && (subjectGroup.id !== id);
            } else {
                return subjectGroup?.name?.toLocaleLowerCase()?.trim() === name.toLocaleLowerCase().trim() && (subjectGroup.tempUuid !== id);
            }
        });
        if (duplicateName) {
            return true;
        }
        return false;
    }

    handleErrors() {
        const nameExists = this.prominenceGroups.filter(group => group.nameExists);
        this.errors.promienceSubjectNameExists = nameExists.length > 0;

        const emptyName = this.prominenceGroups.filter(group => group.emptyName);
        this.errors.promienceSubjectNameEmpty = emptyName.length > 0;

        const emptyKeywords = this.prominenceGroups.filter(group => group.emptyKeywords);
        this.errors.promienceSubjectKeywordsEmpty = emptyKeywords.length > 0;

        this.setErrors(this.errors);
        this.changesToSave();
    }

    changesToSave(): boolean {
        // Add other settings checks in here
        if (this.checkProminenceGroupsForChanges()) {
            this.changesToSave$.next(true);
            return true;
        } else {
            this.changesToSave$.next(false);
            return false;
        }
    }

    checkProminenceGroupsForChanges(): boolean {
        let changes = false;
        if (this.prominenceGroups) {
            if (this.prominenceGroups.length !== this.originalProminenceGroups.length) {
                changes = true;
            } else {
                changes = this.prominenceGroups.some(group => {
                    const oldGroup = this.originalProminenceGroups.find(originalGroup => originalGroup.id === group.id);
                    if (oldGroup) {
                        return !group.compare(oldGroup);
                    } else {
                        return true;
                    }
                });
            }
        }
        return changes;
    }

    showCanLeaveDialog(): Observable<boolean> {
        if (!this.changesToSave()) {
            return of(true);
        }
        return new Observable(
            (observer: Observer<boolean>) => {
                const io: ConfirmDialogModel = {
                    inputs: {
                        heading: 'DeactivateGuard.heading',
                        messageHtml: 'DeactivateGuard.message',
                        okLabel: 'continue',
                        id: 'AccountPrefsContinue'
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed) {
                                this.resetChanges();
                            }
                            observer.next(confirmed);
                            observer.complete();
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, io);
            }
        );
    }

    resetChanges(): void {
        this.prominenceGroups = this.originalProminenceGroups.map(group => new ProminenceGroupModel(group));
        this.prominenceGroups$.next(this.prominenceGroups);
        this.changesToSave$.next(false);
    }
}
